import React, { useState } from "react";
import { api } from "@/lib/api";
import { usePaywallGuard } from "@/lib/usePaywallGuard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PlaceInput from "@/components/PlaceInput";
import DatePicker from "@/components/DatePicker";
import { Heart, Sparkles, Star, AlertTriangle, CheckCircle2 } from "lucide-react";

const KOOT_LABELS = {
  Varna: "Varna (Spiritual)",
  Vashya: "Vashya (Attraction)",
  Tara: "Tara (Wellbeing)",
  Yoni: "Yoni (Intimacy)",
  Graha_Maitri: "Graha Maitri (Mental)",
  Gana: "Gana (Temperament)",
  Bhakoot: "Bhakoot (Family)",
  Nadi: "Nadi (Health & Progeny)",
};

const emptyPerson = () => ({ name: "", dob: "", time: "12:00", city: null });

export default function Matching() {
  const guard = usePaywallGuard();
  const [boy, setBoy] = useState(emptyPerson());
  const [girl, setGirl] = useState(emptyPerson());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildPerson = (p) => ({
    name: p.name || null,
    dob: p.dob,
    time: p.time,
    lat: p.city.lat,
    lon: p.city.lon,
    tz: p.city.tz,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!boy.city || !girl.city) {
      setError("Please select birth city for both partners");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const data = await guard(
        async () => (await api.post("/astro/guna-milan", {
          boy: buildPerson(boy),
          girl: buildPerson(girl),
        })).data,
        "Compatibility Match"
      );
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const ratingColor = result ? (
    result.total_score >= 25 ? "text-emerald-300" :
    result.total_score >= 18 ? "text-amber-300" :
    "text-rose-300"
  ) : "";

  return (
    <div className="space-y-6" data-testid="matching-page">
      <header>
        <h1 className="font-serif text-4xl text-slate-50 mb-2">Compatibility Match</h1>
        <p className="text-slate-400">Ashtakoot Guna Milan — 8 koots, 36 points + AI-powered relationship insight.</p>
      </header>

      <Card className="glass border-white/10 p-6">
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="match-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <PersonForm side="boy" label="Partner 1" person={boy} setPerson={setBoy} />
            <PersonForm side="girl" label="Partner 2" person={girl} setPerson={setGirl} />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="match-submit-btn"
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 rounded-full font-medium px-8 py-5 gap-2"
          >
            <Heart className="w-4 h-4" />
            {loading ? "Computing compatibility..." : "Match Charts"}
          </Button>
          {error && <div className="text-sm text-rose-400" data-testid="match-error">{error}</div>}
        </form>
      </Card>

      {result && (
        <div className="space-y-5 animate-fade-up" data-testid="match-result">
          {/* Overall score */}
          <Card className="glass border-white/10 p-8 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6 items-center">
              <div className="text-center">
                <div className={`font-serif text-7xl ${ratingColor}`} data-testid="match-score">{result.total_score}</div>
                <div className="text-slate-400 text-sm">of {result.max_score} ({result.percentage}%)</div>
                <div className={`mt-2 text-lg font-medium ${ratingColor}`} data-testid="match-rating">{result.rating}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-amber-300 mb-2">Verdict</div>
                <p className="text-lg text-slate-100 font-serif italic leading-snug">"{result.verdict}"</p>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                    <div className="text-amber-300 text-xs uppercase tracking-wider mb-1">Partner 1</div>
                    <div className="text-slate-100">{result.boy.moon_sign}</div>
                    <div className="text-xs text-slate-400">{result.boy.nakshatra} • {result.boy.nakshatra_lord}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-slate-900/40 border border-white/5">
                    <div className="text-amber-300 text-xs uppercase tracking-wider mb-1">Partner 2</div>
                    <div className="text-slate-100">{result.girl.moon_sign}</div>
                    <div className="text-xs text-slate-400">{result.girl.nakshatra} • {result.girl.nakshatra_lord}</div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* Koot breakdown */}
          <Card className="glass border-white/10 p-6" data-testid="koot-breakdown">
            <h3 className="font-serif text-2xl text-slate-50 mb-4 flex items-center gap-2">
              <Star className="w-5 h-5 text-gold" /> 8-Koot Breakdown
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(result.koots).map(([key, koot]) => {
                const pct = (koot.score / koot.max) * 100;
                const good = pct >= 60;
                const bad = pct === 0;
                return (
                  <div
                    key={key}
                    data-testid={`koot-${key}`}
                    className={`p-4 rounded-xl border ${
                      bad ? "bg-rose-500/10 border-rose-500/30" :
                      good ? "bg-emerald-500/10 border-emerald-500/30" :
                      "bg-slate-900/40 border-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs uppercase tracking-wider text-amber-300">{KOOT_LABELS[key]}</div>
                      {bad ? <AlertTriangle className="w-4 h-4 text-rose-400" /> :
                       good ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : null}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="font-serif text-3xl text-slate-50">{koot.score}</span>
                      <span className="text-sm text-slate-400">/ {koot.max}</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden">
                      <div
                        className={`h-full ${bad ? "bg-rose-500" : good ? "bg-emerald-500" : "bg-amber-500"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* AI insight */}
          {result.ai_insight && (
            <Card className="glass border-white/10 p-6" data-testid="ai-insight">
              <h3 className="font-serif text-2xl text-slate-50 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold" /> AI Relationship Insight
              </h3>
              <div className="text-slate-200 leading-relaxed whitespace-pre-wrap text-sm">
                {result.ai_insight}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function PersonForm({ side, label, person, setPerson }) {
  const update = (k, v) => setPerson({ ...person, [k]: v });
  return (
    <div className="space-y-3 p-4 rounded-xl bg-slate-900/30 border border-white/5">
      <h3 className="font-serif text-xl text-amber-300">{label}</h3>
      <div>
        <Label className="text-slate-300 text-xs">Name (optional)</Label>
        <Input
          value={person.name}
          onChange={(e) => update("name", e.target.value)}
          data-testid={`match-${side}-name`}
          className="mt-1 bg-slate-900/50 border-white/10 text-white"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-slate-300 text-xs">DOB</Label>
          <div className="mt-1">
            <DatePicker
              value={person.dob}
              onChange={(v) => update("dob", v)}
              testId={`match-${side}-dob`}
              placeholder="Pick DOB"
              className="text-sm h-9"
            />
          </div>
        </div>
        <div>
          <Label className="text-slate-300 text-xs">Time</Label>
          <Input
            type="time"
            required
            value={person.time}
            onChange={(e) => update("time", e.target.value)}
            data-testid={`match-${side}-time`}
            className="mt-1 bg-slate-900/50 border-white/10 text-white"
          />
        </div>
      </div>
      <div>
        <Label className="text-slate-300 text-xs">Birth Place</Label>
        <div className="mt-1">
          <PlaceInput
            value={person.city}
            onChange={(c) => update("city", c)}
            testId={`match-${side}-city`}
          />
        </div>
      </div>
    </div>
  );
}
